// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, Phone, Check, X } from "lucide-react";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface PhoneRow {
  id: string;
  phone_e164: string;
  is_primary: boolean;
  is_verified: boolean;
  consent_transactional: boolean;
  opted_out_at: string | null;
  verification_sent_at: string | null;
}

function formatPhoneDisplay(e164: string): string {
  if (!e164) return "";
  if (e164.startsWith("+1") && e164.length === 12) {
    const d = e164.slice(2);
    return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return e164;
}

const PhoneNumbersSection: React.FC = () => {
  const { user } = useAuth();
  const [phones, setPhones] = useState<PhoneRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const refetch = async () => {
    if (!user?.id) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("user_phone_numbers")
      .select("id, phone_e164, is_primary, is_verified, consent_transactional, opted_out_at, verification_sent_at")
      .eq("user_id", user.id)
      .order("created_at", { ascending: true });
    if (error) toast.error("Failed to load phones: " + error.message);
    setPhones((data as PhoneRow[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    refetch();
  }, [user?.id]);

  const removePhone = async (id: string) => {
    if (!confirm("Remove this phone number?")) return;
    const { error } = await supabase.from("user_phone_numbers").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Phone removed");
    refetch();
  };

  const reOptIn = async (id: string) => {
    if (!user?.id) return;
    if (!confirm("Re-enable SMS for this number? You will start receiving transactional messages again.")) return;
    const { error } = await supabase
      .from("user_phone_numbers")
      .update({ opted_out_at: null })
      .eq("id", id)
      .eq("user_id", user.id);
    if (error) return toast.error(error.message);
    toast.success("Re-opted in to SMS");
    refetch();
  };

  const setPrimary = async (id: string) => {
    if (!user?.id) return;
    await supabase.from("user_phone_numbers").update({ is_primary: false }).eq("user_id", user.id);
    const { error } = await supabase.from("user_phone_numbers").update({ is_primary: true }).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Primary phone updated");
    refetch();
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-2">
        <Phone className="h-5 w-5 text-gray-700" />
        <h2 className="text-xl font-semibold text-gray-800">Phone Numbers</h2>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Add a phone number to receive transactional SMS from events you're registered with
        (schedule changes, weather delays, check-in reminders). Reply STOP at any time to opt out.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : phones.length > 0 ? (
        <ul className="divide-y divide-gray-200 mb-4">
          {phones.map((p) => (
            <li key={p.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-medium text-gray-900">{formatPhoneDisplay(p.phone_e164)}</span>
                {p.is_verified ? (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                    <Check className="h-3 w-3" /> Verified
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                    Unverified
                  </span>
                )}
                {p.is_primary && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Primary</span>
                )}
                {p.opted_out_at && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">
                    <X className="h-3 w-3" /> Opted out
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm">
                {p.opted_out_at && (
                  <button onClick={() => reOptIn(p.id)} className="text-blue-600 hover:text-blue-700">
                    Re-opt in
                  </button>
                )}
                {!p.is_verified && (
                  <PhoneAddForm
                    inlinePhone={p.phone_e164}
                    onDone={refetch}
                    onCancel={() => {}}
                    asLink
                  />
                )}
                {!p.is_primary && p.is_verified && (
                  <button onClick={() => setPrimary(p.id)} className="text-gray-700 hover:text-gray-900">
                    Set primary
                  </button>
                )}
                <button onClick={() => removePhone(p.id)} className="text-red-600 hover:text-red-700">
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500 mb-4">No phone numbers yet.</p>
      )}

      {!adding ? (
        <button
          onClick={() => setAdding(true)}
          className="inline-flex items-center px-5 py-2.5 rounded-md bg-primary-700 text-white hover:bg-primary-800 text-sm font-medium"
          style={{ backgroundColor: "#002E62" }}
        >
          + Add Phone Number
        </button>
      ) : (
        <PhoneAddForm
          onDone={() => { setAdding(false); refetch(); }}
          onCancel={() => setAdding(false)}
        />
      )}
    </div>
  );
};

interface PhoneAddFormProps {
  onDone: () => void;
  onCancel: () => void;
  inlinePhone?: string;
  asLink?: boolean;
}

const PhoneAddForm: React.FC<PhoneAddFormProps> = ({ onDone, onCancel, inlinePhone, asLink }) => {
  const [open, setOpen] = useState(!asLink);
  const [step, setStep] = useState<1 | 2>(inlinePhone ? 2 : 1);
  const [phoneInput, setPhoneInput] = useState(inlinePhone ?? "");
  const [codeInput, setCodeInput] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const sendCode = async (phone: string) => {
    setSending(true);
    const { data, error } = await invokeEdgeFunction("send-phone-verification-code", {
      body: { phone },
    });
    setSending(false);
    if (error || !data?.ok) {
      toast.error(data?.error || error?.message || "Failed to send code");
      return false;
    }
    toast.success("Code sent");
    setResendCooldown(60);
    return true;
  };

  const handleSendInitial = async () => {
    const ok = await sendCode(phoneInput);
    if (ok) setStep(2);
  };

  const handleVerify = async (code: string) => {
    setVerifying(true);
    const { data, error } = await invokeEdgeFunction("verify-phone-code", {
      body: { phone: phoneInput, code },
    });
    setVerifying(false);
    if (error || !data?.ok) {
      toast.error(data?.error || error?.message || "Verification failed");
      setCodeInput("");
      return;
    }
    toast.success("Phone verified!");
    onDone();
  };

  if (asLink && !open) {
    return (
      <button
        onClick={async () => {
          setOpen(true);
          await sendCode(inlinePhone!);
        }}
        className="text-blue-600 hover:text-blue-700"
      >
        Verify
      </button>
    );
  }

  return (
    <div className="border border-gray-200 rounded-md p-4 bg-gray-50 mt-3 w-full max-w-md">
      {step === 1 ? (
        <>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone number</label>
          <div className="flex gap-2">
            <input
              type="tel"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              placeholder="(555) 123-4567"
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
            <button
              onClick={handleSendInitial}
              disabled={sending || !phoneInput}
              className="px-4 py-2 rounded-md text-white text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: "#002E62" }}
            >
              {sending ? "Sending…" : "Send Code"}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            We'll text you a 6-digit code. Standard message rates apply. By verifying, you agree to receive
            transactional SMS from RLevents (registration confirmations, schedule updates, etc.).
            Reply STOP at any time to opt out.
          </p>
          <div className="mt-3 text-right">
            <button onClick={onCancel} className="text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-700 mb-3">
            We sent a code to <span className="font-medium">{formatPhoneDisplay(phoneInput)}</span>.
          </p>
          <label className="block text-sm font-medium text-gray-700 mb-1">6-digit code</label>
          <input
            type="text"
            inputMode="numeric"
            value={codeInput}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
              setCodeInput(digits);
              if (digits.length === 6) handleVerify(digits);
            }}
            placeholder="000000"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-2xl font-mono tabular-nums text-center tracking-widest"
            autoFocus
            disabled={verifying}
          />
          <div className="flex justify-between items-center mt-3">
            <button
              onClick={() => sendCode(phoneInput)}
              disabled={resendCooldown > 0 || sending}
              className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
            </button>
            <button onClick={onCancel} className="text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          </div>
        </>
      )}
    </div>
  );
};

export default PhoneNumbersSection;
